/**
 * Post-transfer welcome flow (NOS-169 follow-up): the new owner's guided
 * setup after accepting a transfer. Two steps — connect Stripe, confirm legal
 * identity — and explicitly NO site-building prompts: the org already has a
 * live site, and this flow must never suggest changing it.
 */
import { useCallback, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ArrowRight, Check, Circle, CreditCard, Loader2, RefreshCw } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { LegalIdentityCard } from "@/components/settings/LegalIdentityCard"
import { supabase } from "@/lib/supabase"
import {
  STRIPE_CONNECT_COMPLETE,
  connectStripeAccount,
  getStripeOnboardingLink,
  openStripeOnboardingPopup,
} from "@/lib/stripe-connect"
import { useOrgStore } from "@/stores/orgStore"

export function TransferWelcomePage() {
  const navigate = useNavigate()
  const { currentOrg, fetchUserOrgs } = useOrgStore()
  const [stripeComplete, setStripeComplete] = useState<boolean | null>(null)
  const [stripeBusy, setStripeBusy] = useState(false)
  const [checking, setChecking] = useState(false)

  const refreshStripeStatus = useCallback(async () => {
    if (!currentOrg?.id) return
    setChecking(true)
    const { data } = await supabase
      .from("organizations")
      .select("stripe_onboarding_complete")
      .eq("id", currentOrg.id)
      .maybeSingle()
    setStripeComplete(data?.stripe_onboarding_complete === true)
    setChecking(false)
  }, [currentOrg?.id])

  useEffect(() => {
    refreshStripeStatus()
  }, [refreshStripeStatus])

  // The Stripe onboarding popup posts back when the flow completes.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data === STRIPE_CONNECT_COMPLETE) {
        refreshStripeStatus()
        fetchUserOrgs()
      }
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [refreshStripeStatus, fetchUserOrgs])

  const handleConnectStripe = async () => {
    if (!currentOrg?.id) return
    setStripeBusy(true)
    try {
      const { error: createError } = await connectStripeAccount(currentOrg.id)
      if (createError) {
        toast.error(createError)
        return
      }
      const { data, error: linkError } = await getStripeOnboardingLink(
        currentOrg.id,
        window.location.origin
      )
      if (linkError || !data?.url) {
        toast.error(linkError ?? "Could not get onboarding link")
        return
      }
      openStripeOnboardingPopup(data.url)
    } finally {
      setStripeBusy(false)
    }
  }

  if (!currentOrg) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12">
      <div className="mx-auto max-w-xl space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Welcome to {currentOrg.name}</h1>
          <p className="mt-2 text-muted-foreground">
            You're now the owner. Your website, donors, and history are already in place — two
            quick steps and donations flow to your own account.
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              {stripeComplete ? (
                <Check className="h-5 w-5 text-green-600" aria-hidden />
              ) : (
                <Circle className="h-5 w-5 text-gray-300" aria-hidden />
              )}
              <CardTitle className="text-base">1. Connect your Stripe account</CardTitle>
            </div>
            <CardDescription>
              Donations are paused until your organization has its own Stripe account.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {stripeComplete ? (
              <p className="text-sm text-green-700">Stripe is connected — donations can resume.</p>
            ) : (
              <>
                <Button size="sm" onClick={handleConnectStripe} disabled={stripeBusy}>
                  <CreditCard className="mr-2 h-4 w-4" aria-hidden />
                  {stripeBusy ? "Opening Stripe..." : "Connect Stripe"}
                </Button>
                <Button size="sm" variant="outline" onClick={refreshStripeStatus} disabled={checking}>
                  <RefreshCw className={`mr-2 h-4 w-4 ${checking ? "animate-spin" : ""}`} aria-hidden />
                  Check status
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <div className="space-y-2">
          <div className="flex items-center gap-2 px-1">
            <Circle className="h-5 w-5 text-gray-300" aria-hidden />
            <span className="text-base font-semibold">2. Confirm your legal identity</span>
          </div>
          <p className="px-1 text-sm text-muted-foreground">
            Your EIN and legal name go on donation receipts now that you receive donations
            directly.
          </p>
          <LegalIdentityCard orgId={currentOrg.id} />
        </div>

        <div className="flex items-center justify-between rounded-lg border bg-white p-4">
          <p className="text-sm text-muted-foreground">
            Your site is live and unchanged — edit it anytime from the dashboard.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")}>
            Go to dashboard
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          You can finish these steps later — find them again on your{" "}
          <Link to="/dashboard" className="underline">
            dashboard
          </Link>
          .
        </p>
      </div>
    </div>
  )
}
